// src/constants/thirdPersonThemes.js
//
// TB-108 Phase 2 -- Purpose-built question set for third-party (observer-vantage) interviews.
//
// Used when isCelebration === true for ALL contributor relationships, including partner.
// NOT a replacement for promptPacks.js. Complementary structure.
//
// All Celebration book contributors (partner, child, parent, sibling, friend, observer)
// write ABOUT the recipient and assemble in third person. The relationship selects the
// branch (question overlay), not the person framing. TB-108 corrected model.
//
// Design principles (grounded in docs/third-person-interview-spec.md):
//   - Every question is answerable from direct firsthand experience (Principle 1).
//   - Questions invite scenes, not summaries (Principle 2).
//   - The smallest specific detail is the goal, not big-picture characterisation (Principle 3).
//   - Relationship-specific vantage unlocks branch questions (Principle 4).
//   - Character emerges through interactions, not adjectives (Principle 5).
//   - Ordinary days over milestone days (Principle 6).
//   - In-Memory requires a gentler tone contract and safer sequencing (Principle 7).
//   - Never ask the contributor to ventriloquize the subject (Principle 8).
//
// [Name] is replaced at render time with recipientName.
// No em-dashes. No banned sentiment words (cherish, legacy, preserve).
//
// TB-108 Phase 2 Final: branch taxonomy expanded.
//   'child'    -- writer is the son/daughter; subject is their parent.
//                 Questions reference the parent as parent: earliest memories of them,
//                 the moment you saw them as a full person, lessons they passed down.
//   'parent'   -- writer is the parent; subject is their child.
//                 Questions reference the child as child: watching them grow, who they
//                 were becoming, what you hope is remembered about them as a person.
//   'sibling'  -- writer is a sibling (brother or sister); subject is their sibling.
//                 Questions centre on shared childhood and household, the lifelong bond,
//                 and how the relationship shifted from kids to adults.
//   'friend'   -- close friend or best friend.
//   'observer' -- coworker, colleague, all other / default.

// ---------------------------------------------------------------------------
// TB-108 BACKLOG -- deferred fixes (TB-108 Final Cleanup round, 2026-06-25)
// Do NOT address without real customer transcripts grounding the reorder.
// ---------------------------------------------------------------------------
//
// rank 2 (MED) -- FIRST-THREE REGISTER VARIETY
//   Partner IN_MEMORY: TPOD-1-IM and hoisted TPOD-7-P both chase morning, giving two morning
//   scene asks back-to-back. Hoist TPOD-2-IM (evening) instead, or reframe TPOD-1-IM to a
//   whole-arc question when TPOD-7-P is present.
//   Child IN_MEMORY: TPOD-1-IM (morning) + TPOD-3 (evening) bookend without contrast in the
//   first three. Keep one day-shape question and push TPOD-2-IM to slot 4+; hoist TPOD-4
//   (greeting/voice) into slot 3.
//   Grandchild: opener + TPMT-7-GC + TPMT-1 are three signature-memory swings. Reorder to:
//   opener, TPMT-7-GC, TPMT-8-GC (carry-forward), then TPMT-1 at slot 3+.
//   Child what-makes: TPMT-1 and TPMT-3 are near-duplicate domestic-habit asks. Reshape
//   TPMT-1 toward a different facet (saying or surprise) so they do not double-up.
//   Sibling: surface both sibling overlays in the first three; push generic TPQH-2 down.
//   Risk: reordering the assembly path for 5+ scenarios is regression-heavy without transcripts.
//
// rank 3 (MED) -- BEST-FIT VEIN NOT FRONT-LOADED
//   FATHERS_DAY descendant: interleave TPTM-1 (what they taught me) into the first theme or
//   lead with what-they-taught-me.
//   RETIREMENT observer: put TPTM-7-OB (most important thing you learned) in the first two themes.
//   GRANDPARENTS_DAY grandchild: promote a handed-down-story prompt into the first theme's first three.
//   IN_MEMORY child: when a "who they were as a person" intent signal exists, interleave a TPWW
//   person-first question into the first 3-4 questions.
//   Risk: requires an intent-signal capture step in the book wizard.
//
// rank 8 (MED) -- ASSESSMENT-HARNESS ACCURACY
//   Persona simulator traces constants modules (getThirdPersonPrompts) and misses the live
//   Q0_COLLISION_SUPPRESSES step in StRecordPage.js (L~2588). Fix the harness so every
//   scenario's reviewed first-three matches what the contributor actually sees after assembly.
//   This is a TEST-HARNESS note, not an app fix.
//
// rank 9 (LOW) -- OVER-STACKED QUALIFIERS
//   TPMT-3 child/grandchild: hard-coded "at the stove, on the phone, in the car" is a
//   domestic-guess buffet that misses for many subjects. Reduce to one neutral anchor.
//   TPOD-2-IM: front-loads two hedges and three qualifiers before its concrete ask.
//   IN_MEMORY child trio: all three questions use "it doesn't have to be special" reassurance.
//   Keep one; let the other two ask directly.
//
// rank 10 (LOW) -- TPQH-2 GENERIC NOUN ASK
//   TPQH-2 pulls for a bare noun (song/food/object) rather than a scene and is wasted in
//   slot 2 of the sibling first-three. Add a scene hook and push it out of sibling slot 2.
//
// ---------------------------------------------------------------------------
// getBranchType -- sub-branch within observer vantage
// ---------------------------------------------------------------------------

/**
 * Maps an authorRelationship string to a relationship sub-branch.
 * Called after getAuthorVantage() has confirmed the vantage is 'observer'.
 *
 * Sub-branches:
 *   'partner'  -- already handled upstream; kept here as a guard
 *   'child'    -- writer is the son/daughter; subject is their parent
 *   'parent'   -- writer is the parent; subject is their child
 *   'sibling'  -- writer is a sibling (brother or sister); shared-childhood access
 *   'friend'   -- close friend or best friend
 *   'observer' -- coworker, colleague, all other / default
 *
 * The 'child' and 'parent' branches are directionally opposite and must NOT share
 * directional wording. 'child' questions reference the subject as a parent;
 * 'parent' questions reference the subject as a child.
 *
 * Matching is case-insensitive and liberal -- unknown strings return 'observer' safely.
 *
 * @param {string} authorRelationship -- free-text field from the book wizard
 * @returns {'partner'|'child'|'parent'|'sibling'|'friend'|'observer'}
 */
export function getBranchType(authorRelationship) {
  if (!authorRelationship || typeof authorRelationship !== 'string') return 'observer';
  const r = authorRelationship.toLowerCase().trim();

  // Partner guard (should be caught upstream but handled defensively)
  const PARTNER_EXACT = ['partner', 'spouse', 'husband', 'wife', 'boyfriend', 'girlfriend', 'fiance', 'fiancee', 'fiancé', 'fiancée', 'so'];
  const PARTNER_PHRASE = ['significant other', 'life partner'];
  if (PARTNER_EXACT.some(t => r === t)) return 'partner';
  if (PARTNER_PHRASE.some(t => r.includes(t))) return 'partner';

  // Sibling branch: brother or sister. Checked before child/parent so 'brother'/'sister'
  // never falls into the child branch via substring matching (none overlap, but explicit order is safer).
  const SIBLING_TERMS = ['sibling', 'brother', 'sister'];
  if (SIBLING_TERMS.some(t => r === t || r.includes(t))) return 'sibling';

  // Parent branch: writer IS the parent; subject is their child.
  // Values from the dropdown: 'parent'. Free-text equivalents below.
  // 'dad'/'mom' without 'step'/'grand' context typically means the writer IS describing
  // their own child only when the exact value is the parent-direction dropdown value.
  // For safety, match on words that unambiguously mean "I am the parent of this person".
  const PARENT_TERMS = ['parent', 'mom', 'mother', 'dad', 'father'];
  if (PARENT_TERMS.some(t => r === t)) return 'parent';
  // Grandparent dropdown value: grandparent writing about their grandchild -- ancestor vantage
  const GRANDPARENT_TERMS = ['grandparent', 'grandmother', 'grandfather', 'grandma', 'grandpa', 'nana', 'nanny', 'granny', 'poppa', 'grammy'];
  if (GRANDPARENT_TERMS.some(t => r === t || r.includes(t))) return 'parent';
  // Phrase-match: "my son's mom", "father of", "mother of"
  if (/\b(father|mother|mom|dad|parent)\s+of\b/.test(r)) return 'parent';

  // Grandchild branch: must be checked BEFORE child so 'grandchild'/'grandson'/etc.
  // do not fall through to the 'child' substring match below.
  const GRANDCHILD_TERMS = ['grandchild', 'grandson', 'granddaughter', 'grandkid'];
  if (GRANDCHILD_TERMS.some(t => r === t || r.includes(t))) return 'grandchild';

  // Child branch: writer IS the son/daughter; subject is their parent.
  // Guard: exclude 'grand' prefix so 'grandchild' does not double-match here.
  const CHILD_TERMS = ['son', 'daughter', 'child', 'kid'];
  if (CHILD_TERMS.some(t => r === t || (r.includes(t) && !r.includes('grand')))) return 'child';

  // Friend branch: close or best friends.
  const FRIEND_TERMS = ['friend'];
  if (FRIEND_TERMS.some(t => r === t || r.includes(t))) return 'friend';

  // Coworker / colleague / everything else
  return 'observer';
}

// ---------------------------------------------------------------------------
// THIRD_PERSON_THEMES -- the 7-theme question set
// ---------------------------------------------------------------------------

/**
 * Structured theme set for observer-vantage third-party interviews.
 *
 * Each theme:
 *   id          -- kebab-case identifier
 *   name        -- human-readable label
 *   core        -- questions usable for any relationship (always served first)
 *   partner     -- additional questions for the partner branch
 *   child       -- writer is son/daughter; subject is their parent
 *   parent      -- writer is the parent; subject is their child
 *   sibling     -- writer is a sibling; shared-childhood vantage
 *   friend      -- additional questions for the friend branch
 *   occasionNotes -- guidance on question ordering/weighting by occasion
 *
 * Each question:
 *   id    -- spec-reference id (e.g. TPIP-1)
 *   text  -- the question string; [Name] is replaced at render time
 */
export const THIRD_PERSON_THEMES = [
  // -------------------------------------------------------------------------
  // Theme 1: How I Met Them / First Impressions
  // -------------------------------------------------------------------------
  {
    id: 'how-i-met-them',
    name: 'How I Met Them',
    occasionNotes: {
      IN_MEMORY: 'Strong opening theme for memorial books. Grounds the contributor in their own experience and establishes vantage. Prefer TPIP-1 as opener.',
      default: 'Good universal opener. Establishes the contributor\'s specific vantage and grounds the relationship.',
    },
    core: [
      { id: 'TPIP-1', text: 'Take me back to when you first met [Name]. Where were you, and what do you remember noticing about [them] right away?' },
      { id: 'TPIP-2', text: 'What was your first impression of [Name], and how much of it turned out to be right?' },
      { id: 'TPIP-3', text: 'Tell me about a specific thing [Name] did or said early on that made you think, I want to know this person.' },
      { id: 'TPIP-4', text: 'Where were you the first time you really talked, the first time it went beyond small talk? What were you talking about?' },
    ],
    partner: [
      { id: 'TPIP-5-P', text: 'Tell me about the first time you saw [Name] across a room, or the first time you heard [their] name. What was the exact moment?' },
      { id: 'TPIP-6-P', text: 'Was there a moment early on when you knew something was different about this one? Take me to that moment specifically.' },
    ],
    child: [
      // Writer is the son/daughter; subject is their parent.
      { id: 'TPIP-5-C', text: 'Tell me about the earliest memory you have of [Name]. You don\'t have to start with something big. Just the first image that comes to mind.' },
      { id: 'TPIP-6-C', text: 'Was there a point, maybe as you got older, when you started seeing [Name] as a whole person and not just your parent? It might have been one moment or something that crept up slowly. Tell me about it.' },
    ],
    parent: [
      // Writer is the parent; subject is their child.
      { id: 'TPIP-5-PA', text: 'Tell me about the first time you really looked at [Name] and thought, this is exactly who [Name] is. Where were you, and what was [Name] doing?' },
      { id: 'TPIP-6-PA', text: 'What is one of the earliest moments you remember with [Name] that still comes back to you clearly? What was [Name] like back then?' },
    ],
    sibling: [
      // Writer is a sibling; shared childhood and household vantage.
      { id: 'TPIP-5-SB', text: 'What is one of your earliest memories of [Name] from when you were both kids? It doesn\'t have to be a big moment, just something that has stayed with you.' },
      { id: 'TPIP-6-SB', text: 'Was there a moment, maybe when you were older, when you saw [Name] as a full person and not just your sibling? What happened?' },
    ],
    friend: [
      { id: 'TPIP-5-F', text: 'How did you and [Name] go from acquaintances to actually close? Was there a specific moment, or did it just happen?' },
      { id: 'TPIP-6-F', text: 'Tell me about an early memory of the two of you that still makes you smile. What were you doing, and what made it stick?' },
    ],
    grandchild: [
      { id: 'TPIP-5-GC', text: 'Tell me your earliest memory of [Name]. It does not have to be a big moment. Just the first image that comes back.' },
      { id: 'TPIP-6-GC', text: 'What story about [Name] from before you were born do you love? One that got handed down to you.' },
    ],
    branchNames: {
      child:      'Earliest Memories',
      grandchild: 'My Earliest Memories',
      parent:     'The Beginning',
      sibling:    'Growing Up Together',
      partner:    'How I Met Them',
      friend:     'How I Met Them',
      observer:   'How I Met Them',
    },
  },

  // -------------------------------------------------------------------------
  // Theme 2: What Makes Them Them -- Through a Story
  // -------------------------------------------------------------------------
  {
    id: 'what-makes-them-them',
    name: 'What Makes Them Them',
    occasionNotes: {
      IN_MEMORY: 'Core character-capture theme. Important for memorial books. No sequencing constraints.',
      default: 'Primary character-capture theme. Prefer as second theme after grounding.',
    },
    core: [
      { id: 'TPMT-1', text: 'Tell me about a time you watched [Name] do something that was so entirely [them], you would have known it was [Name] from across the room.' },
      { id: 'TPMT-2', text: 'If you had to describe [Name] by telling me one story, what story would you tell?' },
      { id: 'TPMT-3', text: 'Tell me about a time you saw [Name] with someone who needed help. A stranger, a kid, someone who was struggling. What did you watch [them] do?' },
      { id: 'TPMT-4', text: 'What does [Name] do when things go sideways? Have you been there for one of those moments? Tell me what that looked like.' },
      { id: 'TPMT-5', text: 'Is there something [Name] cares about deeply that most people wouldn\'t expect? Something that is entirely [theirs]? Tell me how you know that about [them].' },
      { id: 'TPMT-6', text: 'Tell me about a moment you watched [Name] with another person and thought, that right there is who [Name] is.' },
    ],
    partner: [
      { id: 'TPMT-7-P', text: 'What does [Name] do when [they] think no one is watching? Something small, something that is just [them].' },
      { id: 'TPMT-8-P', text: 'Tell me about a moment you looked at [Name] and thought, I am so glad I know this person. What was [Name] doing?' },
    ],
    child: [
      // Writer is the son/daughter; subject is their parent.
      // [parentRole] is resolved at render time from occasion+pronouns:
      //   FATHERS_DAY/he-him -> "your dad"
      //   MOTHERS_DAY/she-her -> "your mom"
      //   unknown -> "your mom or dad"
      { id: 'TPMT-7-C', text: 'Tell me about a moment you realized [Name] was a full person with [their] own life, worries, and joys, separate from being [parentRole]. What happened?' },
      { id: 'TPMT-8-C', text: 'What did [Name] do as a parent that you understand more deeply now than you did then? Tell me about that moment when it finally made sense.' },
    ],
    parent: [
      // Writer is the parent; subject is their child.
      { id: 'TPMT-7-PA', text: 'Tell me about a moment you watched [Name] handle something on [their] own and thought, [Name] is going to be just fine. What were you watching [them] do?' },
      { id: 'TPMT-8-PA', text: 'What did [Name] show you about who [Name] was becoming, something you noticed before [Name] did? Tell me about a specific moment.' },
    ],
    sibling: [
      // Writer is a sibling; shared-childhood vantage.
      { id: 'TPMT-7-SB', text: 'Tell me about something [Name] did when you were both kids that was so entirely [them], you knew even then that it was just who [Name] was.' },
      { id: 'TPMT-8-SB', text: 'How did you see [Name] differently once you were both adults compared to growing up together? Was there a moment that shifted how you saw [them]?' },
    ],
    friend: [
      { id: 'TPMT-7-F', text: 'Tell me about a version of [Name] that the people who only know [them] casually have never seen. When did you first see that version?' },
      { id: 'TPMT-8-F', text: 'Is there something [Name] would never say about [themself] that you have watched [them] be? Tell me a story about it.' },
    ],
    grandchild: [
      { id: 'TPMT-7-GC', text: 'Whenever you were around [Name], was there something [they] did the same way every time, a phrase, a habit, something that felt completely [theirs]?' },
      { id: 'TPMT-8-GC', text: 'What quality of [Name] do you hope you carry forward, something you noticed about [them] and thought, I want that.' },
    ],
    observer: [
      { id: 'TPMT-7-OB', text: 'Tell me about a time you watched [Name] handle something at work, a challenge, a moment of pressure, something that showed you who [Name] is.' },
      { id: 'TPMT-8-OB', text: 'What is something [Name] was good at that most people in that role would not have thought to do?' },
    ],
    branchNames: {
      friend:     'What Makes [Name], [Name]',
      sibling:    'What Makes [Name], [Name]',
      child:      'What Makes [Name], [Name]',
      grandchild: 'What Makes [Name], [Name]',
      parent:     'What Makes [Name], [Name]',
      partner:    'What Makes [Name], [Name]',
      observer:   'What Makes [Name], [Name]',
    },
  },

  // -------------------------------------------------------------------------
  // Theme 3: A Moment I'll Never Forget
  // -------------------------------------------------------------------------
  {
    id: 'a-moment-ill-never-forget',
    name: 'A Moment I\'ll Never Forget',
    occasionNotes: {
      IN_MEMORY: 'Preferred entry points for memorial context are TPMF-1 and TPMF-5. Use TPMF-1 as opener. Defer TPMF-4 (something went wrong) until contributor has warmed up -- never open with it. TPMF-2 should include the soft prefix "Only share what feels right to revisit." Crying is not a stop signal; allow it.',
      default: 'Safe to open with for most occasions. TPMF-2 is the richest single question. For celebration occasions (birthday, retirement) prefer TPMF-1 or TPMF-3 as entry.',
    },
    core: [
      { id: 'TPMF-1', text: 'Tell me about a memory of [Name] that is just yours, something the two of you shared that no one else was there for.' },
      { id: 'TPMF-2', text: 'If you had to point to one moment with [Name] that you carry with you, what would it be? Take me there slowly, like I wasn\'t there.' },
      { id: 'TPMF-3', text: 'Tell me about a time [Name] surprised you, not necessarily pleasantly, just a moment you genuinely did not see coming.' },
      { id: 'TPMF-4', text: 'Tell me about a time you were with [Name] when something went wrong. What happened, and what did you both do?' },
      { id: 'TPMF-5', text: 'Is there a moment with [Name] that you have replayed in your head more than once? Tell me what happens in it.' },
    ],
    partner: [
      { id: 'TPMF-6-P', text: 'Tell me about a moment, maybe an ordinary one, where you knew exactly who [Name] was. Nothing dramatic. Just a moment that has stayed with you.' },
      { id: 'TPMF-7-P', text: 'Tell me about a hard thing you went through together. You don\'t have to share everything. Just what you want the world to know about how [Name] showed up.' },
    ],
    child: [
      // Writer is the son/daughter; subject is their parent.
      { id: 'TPMF-6-C', text: 'Tell me about a moment with [Name] that you would give a lot to go back to. Where were you, and what was happening?' },
      { id: 'TPMF-7-C', text: 'Tell me about a conversation with [Name] that has stayed with you. You don\'t have to remember every word. Just what it felt like and what you walked away carrying.' },
    ],
    parent: [
      // Writer is the parent; subject is their child.
      { id: 'TPMF-6-PA', text: 'Tell me about a moment with [Name] that you have replayed in your head more than once. Where were you, and what was happening?' },
      { id: 'TPMF-7-PA', text: 'Tell me about a time [Name] came to you with something difficult. What did [they] say, and what did you do?' },
    ],
    sibling: [
      // Writer is a sibling; shared-childhood vantage.
      { id: 'TPMF-6-SB', text: 'Tell me about a memory of [Name] from when you were kids that you still think about. Something the two of you shared that no one else would know.' },
      { id: 'TPMF-7-SB', text: 'Tell me about a time as adults when [Name] was there for you, or you were there for [them]. What happened, and what did it show you about who [Name] is?' },
    ],
    friend: [
      { id: 'TPMF-6-F', text: 'Tell me about a moment you and [Name] shared that nobody else was around for. Something that only the two of you know happened.' },
      { id: 'TPMF-7-F', text: 'Is there a time [Name] showed up for you when it mattered? Tell me what that looked like, as specifically as you can.' },
    ],
    grandchild: [
      { id: 'TPMF-6-GC', text: 'Tell me about a time with [Name] you would give a lot to go back to. Where were you, and what was happening?' },
      { id: 'TPMF-7-GC', text: 'What is a story [Name] always told, the one [Name] told in [their] own way? Walk me through it the way [Name] told it.' },
    ],
    observer: [
      { id: 'TPMF-6-OB', text: 'Tell me about a moment you watched [Name] do something at work that stuck with you, something you have thought about since.' },
      { id: 'TPMF-7-OB', text: 'Tell me about a time [Name] went out of [their] way for someone at work. What did you watch [them] do?' },
    ],
  },

  // -------------------------------------------------------------------------
  // Theme 4: What They Taught Me / Their Mark on Me
  // -------------------------------------------------------------------------
  {
    id: 'what-they-taught-me',
    name: 'What They Taught Me',
    occasionNotes: {
      IN_MEMORY: 'Valuable for memorial books. Anchor in the contributor\'s own formation, not in the subject\'s intent. TPTM-5 (showed you how to handle something difficult) can read as grief-adjacent for recent losses -- use later in the sequence, not as an opener.',
      default: 'Strong for family and mentorship occasions (Mother\'s Day, Father\'s Day, Grandparents Day, Retirement). Use after Theme 2 has established character.',
    },
    core: [
      { id: 'TPTM-1', text: 'What is something you know how to do, or know how to be, because of [Name]? How did you actually learn it from [them]?' },
      { id: 'TPTM-2', text: 'Tell me about a time [Name] said something to you that you still think about. What were the circumstances, and what did [they] say?' },
      { id: 'TPTM-3', text: 'What did knowing [Name] change about how you see the world? Can you trace it back to a specific moment?' },
      { id: 'TPTM-4', text: 'Is there a way you have caught yourself doing something that came from [Name]? Something you picked up from [them] without meaning to?' },
      { id: 'TPTM-5', text: 'Tell me about a time [Name] showed you how to handle something difficult. What did you watch [them] do?' },
      { id: 'TPTM-6', text: 'What is the most important thing [Name] ever did for you, not a gift, but something [they] did for who you became?' },
    ],
    partner: [
      { id: 'TPTM-7-P', text: 'What has [Name] taught you about yourself? Tell me about a moment when [they] held up a mirror and you saw something you had not seen before.' },
      { id: 'TPTM-8-P', text: 'What have you become because of this relationship that you would not have been otherwise?' },
    ],
    child: [
      // Writer is the son/daughter; subject is their parent.
      { id: 'TPTM-7-C', text: 'What did [Name] give you that you carry now, even if [they] never knew [they] gave it to you?' },
      { id: 'TPTM-8-C', text: 'Is there a piece of your parent in you that used to embarrass you and now makes you proud? Tell me about that.' },
    ],
    parent: [
      // Writer is the parent; subject is their child.
      { id: 'TPTM-7-PA', text: 'Has watching [Name] grow changed how you see the world, or yourself? Tell me about a moment that shifted something in you.' },
      { id: 'TPTM-8-PA', text: 'What has [Name] taught you, without meaning to? Tell me about something you learned from [them].' },
    ],
    sibling: [
      // Writer is a sibling; shared-childhood vantage.
      { id: 'TPTM-7-SB', text: 'What did you learn from [Name] growing up, something that stayed with you into adulthood? Can you trace it back to a specific moment?' },
      { id: 'TPTM-8-SB', text: 'Is there something you got from [Name], a habit, a way of seeing things, that you still carry? Tell me about how you picked it up.' },
    ],
    friend: [
      { id: 'TPTM-7-F', text: 'Has there been a moment in your own life where you caught yourself thinking, [Name] would know exactly what to do here? What was going on?' },
      { id: 'TPTM-8-F', text: 'What is something [Name] showed you about how to be in the world, not by saying it, just by being around [them]?' },
    ],
    grandchild: [
      { id: 'TPTM-7-GC', text: 'What is something you know how to do, or know how to be, because of [Name]? Can you trace it back to something specific?' },
      { id: 'TPTM-8-GC', text: 'What is a lesson [Name] passed down without saying it in words? Something you picked up from just being around [them].' },
    ],
    observer: [
      { id: 'TPTM-7-OB', text: 'What is the most important thing you learned from working with [Name]? Can you trace it back to a specific moment?' },
      { id: 'TPTM-8-OB', text: 'How did [Name] make the people around [them] better at the work? Tell me something specific you watched.' },
    ],
  },

  // -------------------------------------------------------------------------
  // Theme 5: Their Quirks and Humor
  // -------------------------------------------------------------------------
  {
    id: 'their-quirks-and-humor',
    name: 'Their Quirks and Humor',
    occasionNotes: {
      IN_MEMORY: 'Welcome and valuable in memorial books. Laughter is part of a full portrait, not a distraction from grief. Can be placed early to provide warmth before heavier themes.',
      default: 'Third-party equivalent of Hot Seat. Good for celebration occasions (birthday, retirement). Every question asks for a witnessed moment, not general trivia.',
    },
    core: [
      { id: 'TPQH-1', text: 'Tell me about something small and specific that [Name] always does the same way. A habit, a phrase, a ritual you have watched a hundred times.' },
      { id: 'TPQH-2', text: 'What is the one thing, a song, a food, or an object, that you cannot come across without thinking of [Name]?' },
      { id: 'TPQH-3', text: 'Tell me about the face [Name] makes, the one that tells you everything about what is actually going through [their] mind.' },
      { id: 'TPQH-4', text: 'Tell me about the last time [Name] made you genuinely laugh. What happened?' },
      { id: 'TPQH-5', text: 'What is something [Name] is weirdly, specifically, impressively good at, the kind of thing that should not be a skill but somehow is?' },
      { id: 'TPQH-6', text: 'Tell me about a [Name] opinion, food order, or stubborn preference that tells you more about [them] than a whole conversation would.' },
      { id: 'TPQH-7', text: 'What does [Name] do when fully in [their] element, not performing anything? Where is [Name] when that happens, and what does it look like?' },
      { id: 'TPQH-8', text: 'Tell me about a time [Name] was completely, unguardedly [their] real self in a way that stuck with you.' },
    ],
    // Theme 5: core questions work for all branches including grandchild.
    // No grandchild-specific overlay for this theme; core questions apply universally.
    observer: [
      { id: 'TPQH-9-OB', text: 'What is something [Name] was known for at work, a phrase, a habit, a way of handling things that was just [them]?' },
      { id: 'TPQH-10-OB', text: 'What did [Name] bring to the team beyond the actual work? What was different when [Name] was in the room?' },
      { id: 'TPQH-11-OB', text: 'What is something on [their] desk, or a signature move in a meeting, that everyone on the team associated with [Name]?' },
    ],
    sibling: [
      { id: 'TPQH-9-SB', text: 'Tell me about a quirk of [Name]\'s you can trace straight back to childhood, a habit you watched form, or something you both got from the same place.' },
      { id: 'TPQH-10-SB', text: 'What is the inside joke, nickname, or running bit between you and [Name] that nobody outside the family would fully get?' },
    ],
    friend: [
      { id: 'TPQH-9-F', text: 'What is the inside joke or running bit between you and [Name] that has its own whole story behind it? Tell me how it started.' },
      { id: 'TPQH-10-F', text: 'Tell me about a plan or adventure with [Name] that went sideways in a way only the two of you could have made happen.' },
    ],
    branchNames: {
      observer:  'What They Were Known For',
      sibling:   'What Makes [Name], [Name]',
      friend:    'What Makes [Name], [Name]',
      child:     'Their Quirks and Humor',
      grandchild:'Their Quirks and Humor',
      parent:    'Their Quirks and Humor',
      partner:   'Their Quirks and Humor',
    },
  },

  // -------------------------------------------------------------------------
  // Theme 6: Ordinary Days With Them
  // -------------------------------------------------------------------------
  {
    id: 'ordinary-days',
    name: 'Ordinary Days With Them',
    occasionNotes: {
      IN_MEMORY: 'Lead with this theme in memorial books. Ordinary-day questions feel emotionally safest and produce the most specific, irreplaceable detail. TPOD-1 through TPOD-3 are particularly strong openers for memorial context. Prefer TPOD-1 as the first question when occasion is IN_MEMORY.',
      default: 'Texture layer. Use after character themes (2, 5) to add daily-life depth.',
    },
    core: [
      { id: 'TPOD-1', text: 'Describe a regular day with [Name], not a big occasion, just a normal Tuesday. Where were you, and what did that day usually look like?' },
      { id: 'TPOD-2', text: 'Tell me about the most ordinary version of time with [Name]. The kind of visit or call where nothing happened but it still felt like something.' },
      { id: 'TPOD-3', text: 'Is there a ritual you and [Name] had, something small and consistent that was just yours? Tell me how it started.' },
      { id: 'TPOD-4', text: 'Tell me about the way [Name] greeted you. The specific way. Not just that [Name] was warm, but what [Name] actually did or said.' },
      { id: 'TPOD-5', text: 'What did [Name]\'s house, kitchen, car, or office look like? Walk me into it. What was on the table, or on the shelf?' },
      { id: 'TPOD-6', text: 'Tell me about something you have done with [Name] so many times it became its own kind of language between you.' },
    ],
    partner: [
      // Fix (rank 7): removed "the way it actually was" closer -- TPOD-2-IM already carries that phrase.
      // Reworded so TPOD-7-P stands on its own with a sound/sensory anchor.
      { id: 'TPOD-7-P', text: 'Tell me about what a morning with [Name] used to look like. Not a special one. Just a regular morning. What did the start of it sound like?' },
      { id: 'TPOD-8-P', text: 'What is something about your daily life together that you will miss, or already miss, the kind of thing you did not notice until it was just the two of you doing it?' },
    ],
    child: [
      // Writer is the son/daughter; subject is their parent.
      { id: 'TPOD-7-C', text: 'Tell me about an ordinary day at home with [Name]. The sights, the sounds, the small things that were always the same.' },
      { id: 'TPOD-8-C', text: 'Is there a specific thing [Name] always did or said in your household, something so routine you barely noticed it at the time? What was it?' },
    ],
    parent: [
      // Writer is the parent; subject is their child.
      { id: 'TPOD-7-PA', text: 'Tell me about a regular day with [Name], not a big occasion, just a normal stretch of time together. What did that usually look like?' },
      { id: 'TPOD-8-PA', text: 'Is there something [Name] always did when you were together, something small that was just [theirs]? A habit, a phrase, the way [they] did something particular?' },
    ],
    sibling: [
      // Writer is a sibling; shared-childhood vantage.
      { id: 'TPOD-7-SB', text: 'Tell me about a regular day at home when you were both kids. Not a special occasion, just a normal afternoon or evening together. What did that look like?' },
      { id: 'TPOD-8-SB', text: 'Is there something [Name] always did around the house, a habit or a way [they] did things, that was so consistent you can still picture it now?' },
    ],
    friend: [
      { id: 'TPOD-7-F', text: 'Tell me about a regular hangout with [Name], not a big occasion, just the two of you in the middle of a normal week. What did that usually look like?' },
      { id: 'TPOD-8-F', text: 'Is there something [Name] always did when you were together, a phrase, a habit, a running joke, that became just part of how you two were? Tell me about it.' },
    ],
    grandchild: [
      { id: 'TPOD-7-GC', text: 'Tell me about a visit to [Name], a normal one, not a holiday. What did a regular afternoon there look and feel like?' },
      { id: 'TPOD-8-GC', text: 'Is there something [Name] always had out, or always did when you arrived, something so consistent you still picture it?' },
    ],
    inMemory: [
      { id: 'TPOD-1-IM', text: 'Think back to what a regular day with [Name] used to look like, not a big occasion, just an ordinary day at home. What was usually the first thing that happened?' },
      { id: 'TPOD-2-IM', text: 'Was there a part of the evening, or the end of a day, that was just yours together, something you did most nights without thinking about it? Walk me through it the way it actually was.' },
    ],
    branchNames: {
      child:      'Ordinary Days With [Name]',
      grandchild: 'Ordinary Days With [Name]',
      parent:     'Ordinary Days With [Name]',
      sibling:    'Ordinary Days With [Name]',
      partner:    'Ordinary Days With [Name]',
      friend:     'Ordinary Days With [Name]',
      observer:   'Ordinary Days Together',
    },
  },

  // -------------------------------------------------------------------------
  // Theme 7: What I'd Want the World to Know
  // -------------------------------------------------------------------------
  {
    id: 'what-id-want-the-world-to-know',
    name: 'What I\'d Want the World to Know',
    occasionNotes: {
      IN_MEMORY: 'Closing theme for memorial books. Use the IN_MEMORY variants (TPWW-7-IM, TPWW-8-IM) instead of or in addition to the core questions. TPWW-8-IM (what would you want them to know) is a strong closer.',
      default: 'Closing, meaning-making theme. Use last. Anchored in the teller\'s firsthand experience, not in speculation about the subject.',
    },
    core: [
      { id: 'TPWW-1', text: 'If someone met [Name] for the first time tomorrow, what is one thing you would want that person to know?' },
      { id: 'TPWW-2', text: 'What is something about [Name] that does not show up in a photograph, a title, or a first conversation?' },
      { id: 'TPWW-3', text: 'If this book is the story of who [Name] really is, what is the chapter you most want to make sure is in it?' },
      { id: 'TPWW-4', text: 'What do you hope [Name] knows about the mark [they] have left on you?' },
      { id: 'TPWW-5', text: 'Tell me something about [Name] that makes you glad [they] exist.' },
      { id: 'TPWW-6', text: 'What would you want [Name] to read in this book someday, years from now, and know that someone saw in [them]?' },
    ],
    inMemory: [
      { id: 'TPWW-7-IM', text: 'What do you want people who did not know [Name] to understand about who [Name] really was?' },
      { id: 'TPWW-8-IM', text: 'What would you want [Name] to know, if you could tell [them] one more thing?' },
    ],
    child: [
      // Writer is the son/daughter; subject is their parent.
      { id: 'TPWW-7-C', text: 'What is something about [Name] as a person, not as your parent, that you want people to understand about [them]?' },
      { id: 'TPWW-8-C', text: 'What do you want your own children, or the people who come after you, to know about who [Name] was?' },
    ],
    parent: [
      // Writer is the parent; subject is their child.
      { id: 'TPWW-7-PA', text: 'What is something about [Name] as a person, not as your child, that you want the world to understand about [them]?' },
      { id: 'TPWW-8-PA', text: 'What do you hope [Name] knows about the mark [they] have left on you, even if you have never found the right words to say it?' },
    ],
    sibling: [
      // Writer is a sibling; shared-childhood vantage.
      { id: 'TPWW-7-SB', text: 'What is something about [Name] that only someone who grew up alongside [them] would know? Something that does not show up anywhere else?' },
      { id: 'TPWW-8-SB', text: 'If you could make sure one story about [Name], from when you were kids or from your whole life together, was never forgotten, which one would it be?' },
    ],
    friend: [
      { id: 'TPWW-7-F', text: 'What would someone know about [Name] after ten years of friendship that no first conversation or photograph could show?' },
      { id: 'TPWW-8-F', text: 'If you could make sure one story about [Name] was always told and never forgotten, which one would it be?' },
    ],
    grandchild: [
      { id: 'TPWW-7-GC', text: 'What is something about [Name] that only someone who loved [them] closely would know?' },
      { id: 'TPWW-8-GC', text: 'What do you want people who never met [Name] to understand about who [Name] really was?' },
    ],
  },
];

// ---------------------------------------------------------------------------
// getThirdPersonPrompts -- resolve questions for a given theme + branch + occasion
// ---------------------------------------------------------------------------

/**
 * Returns the ordered list of questions to use for a given theme, relationship branch,
 * and occasion. Core questions come first; branch questions are appended.
 *
 * For IN_MEMORY occasion on Theme 7, the inMemory variants are appended after core.
 * For IN_MEMORY occasion on Theme 3, TPMF-4 (something went wrong) is moved to the end.
 *
 * Branch taxonomy:
 *   'child'   -- writer is son/daughter; subject is their parent
 *   'parent'  -- writer is the parent; subject is their child
 *   'sibling' -- writer is a sibling; shared-childhood vantage
 *   'friend'  -- close friend
 *   'partner' -- romantic partner/spouse
 *   'observer' -- coworker, colleague, default (core questions only)
 *
 * @param {string} themeId       -- one of the THIRD_PERSON_THEMES ids
 * @param {'partner'|'child'|'parent'|'sibling'|'friend'|'observer'} branchType
 * @param {string} [occasionId]  -- from OCCASION_TYPES (e.g. 'IN_MEMORY', 'MOTHERS_DAY')
 * @returns {Array<{id: string, text: string}>}
 */
export function getThirdPersonPrompts(themeId, branchType, occasionId = '') {
  const theme = THIRD_PERSON_THEMES.find(t => t.id === themeId);
  if (!theme) return [];

  const isInMemory = occasionId === 'IN_MEMORY';

  // For descendant branches (child, grandchild, parent, sibling), suppress
  // the how-i-met-them "stranger meeting" core questions (TPIP-1/2/4) which
  // are literally unanswerable for someone born into the relationship.
  const MEETING_QUESTION_IDS = ['TPIP-1', 'TPIP-2', 'TPIP-4'];
  const DESCENDANT_BRANCHES = ['child', 'grandchild', 'parent', 'sibling'];

  let coreQuestions = [...(theme.core || [])];

  if (themeId === 'how-i-met-them' && DESCENDANT_BRANCHES.includes(branchType)) {
    coreQuestions = coreQuestions.filter(q => !MEETING_QUESTION_IDS.includes(q.id));
  }

  // For IN_MEMORY ordinary-days: use past-tense variants for TPOD-1 and TPOD-2
  if (isInMemory && themeId === 'ordinary-days' && theme.inMemory) {
    const pastTenseIds = ['TPOD-1', 'TPOD-2'];
    coreQuestions = coreQuestions.filter(q => !pastTenseIds.includes(q.id));
    // Prepend the past-tense variants at the front
    coreQuestions = [...theme.inMemory, ...coreQuestions];
  }

  // For IN_MEMORY Theme 3: defer TPMF-4 (something went wrong) to the end.
  if (isInMemory && themeId === 'a-moment-ill-never-forget') {
    const deferred = coreQuestions.filter(q => q.id === 'TPMF-4');
    coreQuestions = [...coreQuestions.filter(q => q.id !== 'TPMF-4'), ...deferred];
  }

  // For celebration occasions (not IN_MEMORY), drop TPMF-3 "not necessarily pleasantly"
  // from the first-three pool for the a-moment-ill-never-forget theme.
  // It opens a negative-surprise door that is tonally wrong for a birthday gift book.
  if (!isInMemory && themeId === 'a-moment-ill-never-forget') {
    const tpmf3 = coreQuestions.filter(q => q.id === 'TPMF-3');
    const rest = coreQuestions.filter(q => q.id !== 'TPMF-3');
    coreQuestions = [...rest, ...tpmf3]; // defer to end
  }

  // De-dupe TPMF-1/TPMF-2 back-to-back: put TPMF-2 after TPMF-5
  // so the two "carry with you" asks are separated by at least one other question.
  if (themeId === 'a-moment-ill-never-forget') {
    const tpmf2 = coreQuestions.find(q => q.id === 'TPMF-2');
    if (tpmf2) {
      coreQuestions = coreQuestions.filter(q => q.id !== 'TPMF-2');
      // Insert after TPMF-5, or at position 3 if TPMF-5 not found
      const idx5 = coreQuestions.findIndex(q => q.id === 'TPMF-5');
      const insertAt = idx5 >= 0 ? idx5 + 1 : Math.min(3, coreQuestions.length);
      coreQuestions = [...coreQuestions.slice(0, insertAt), tpmf2, ...coreQuestions.slice(insertAt)];
    }
  }

  // Re-aim TPOD-2 to avoid duplicating TPOD-1 when consecutive.
  // Swap TPOD-2 text to the sensory/detail variant.
  if (themeId === 'ordinary-days') {
    coreQuestions = coreQuestions.map(q => {
      if (q.id === 'TPOD-2') {
        return { ...q, text: 'When you picture being around [Name] on a completely ordinary day, what is the first small sound, smell, or sight that comes back to you?' };
      }
      return q;
    });
  }

  // TPOD-3 "tell me how it started" -> present-recall for long-relationship branches
  const LONG_RELATION_BRANCHES = ['child', 'grandchild', 'partner', 'sibling'];
  if (themeId === 'ordinary-days' && LONG_RELATION_BRANCHES.includes(branchType)) {
    coreQuestions = coreQuestions.map(q => {
      if (q.id === 'TPOD-3') {
        return { ...q, text: 'Is there a ritual you and [Name] had, something small and consistent that was just yours? Walk me through it the way it actually went.' };
      }
      return q;
    });
  }

  // TPIP-3 descendant reframe: "early on / I want to know this person" -> childhood-grounded
  if (themeId === 'how-i-met-them' && DESCENDANT_BRANCHES.includes(branchType)) {
    coreQuestions = coreQuestions.map(q => {
      if (q.id === 'TPIP-3') {
        return { ...q, text: 'One small thing [Name] did when you were young that still feels completely like [them], a gesture or phrase you can still picture.' };
      }
      return q;
    });
  }

  // De-dup TPQH-1 for observer, sibling, and friend: those branches have their own
  // habit/quirk overlay questions (TPQH-9-OB/SB/F) that cover the same territory.
  if (themeId === 'their-quirks-and-humor' && ['observer', 'sibling', 'friend'].includes(branchType)) {
    coreQuestions = coreQuestions.filter(q => q.id !== 'TPQH-1');
  }

  // Defer TPMT-2 out of the first two positions to avoid back-to-back character-summary asks
  if (themeId === 'what-makes-them-them') {
    const tpmt2 = coreQuestions.find(q => q.id === 'TPMT-2');
    if (tpmt2 && coreQuestions.indexOf(tpmt2) < 2) {
      coreQuestions = coreQuestions.filter(q => q.id !== 'TPMT-2');
      coreQuestions = [...coreQuestions.slice(0, 2), tpmt2, ...coreQuestions.slice(2)];
    }
  }

  // For grandchild branch: replace TPMT-1 with a low-contact-friendly variant
  if (branchType === 'grandchild' && themeId === 'what-makes-them-them') {
    coreQuestions = coreQuestions.map(q => {
      if (q.id === 'TPMT-1') {
        return { ...q, text: 'Even if you only saw [Name] a few times a year, there is one moment that is purely [them]. Tell me about that one.' };
      }
      return q;
    });
  }

  // For child branch: replace TPMT-1's "across the room" stranger-vantage with a proximity/domestic one
  if (branchType === 'child' && themeId === 'what-makes-them-them') {
    coreQuestions = coreQuestions.map(q => {
      if (q.id === 'TPMT-1') {
        // Fix (rank 6): avoid "[they] doing?" which renders "What was they doing?" for they/them subjects.
        // Reworded to "Tell me what that looked like." -- no subject-verb agreement trap.
        return { ...q, text: 'Tell me about a time [Name] did something so completely [them] you could have spotted it from the other end of the house. The kind of thing only someone who grew up with [them] would recognize. Tell me what that looked like.' };
      }
      return q;
    });
  }

  // For child/grandchild branch: replace TPMT-3 with a domestic-vantage variant
  if (['child', 'grandchild'].includes(branchType) && themeId === 'what-makes-them-them') {
    coreQuestions = coreQuestions.map(q => {
      if (q.id === 'TPMT-3') {
        return { ...q, text: 'Tell me about a small thing [Name] did around the house, at the stove, on the phone, in the car, that was so completely [them].' };
      }
      return q;
    });
  }

  // Get the branch-specific overlay questions
  let branchOverlay = [];
  if (branchType === 'partner' && theme.partner) branchOverlay = theme.partner;
  else if (branchType === 'child' && theme.child) branchOverlay = theme.child;
  else if (branchType === 'grandchild' && theme.grandchild) branchOverlay = theme.grandchild;
  else if (branchType === 'parent' && theme.parent) branchOverlay = theme.parent;
  else if (branchType === 'sibling' && theme.sibling) branchOverlay = theme.sibling;
  else if (branchType === 'friend' && theme.friend) branchOverlay = theme.friend;
  else if (branchType === 'observer' && theme.observer) branchOverlay = theme.observer;

  // FRONT-LOAD branch overlay: for non-observer branches, put the first branch
  // question at position 0 (or after any grief on-ramp), replacing any
  // mis-fitting core question that duplicates the same slot.
  // For observer branch: prepend the overlay so witnessed questions open first.
  //
  // Exception: when IN_MEMORY ordinary-days inMemory variants were prepended,
  // they occupy positions 0-1 as a protected on-ramp; branch overlay starts at slot 2.
  // Rank 4+5: 3-slot on-ramp for IN_MEMORY ordinary-days: hoist branch scene question
  // to slot 1 (between TPOD-1-IM and TPOD-2-IM) for concrete grounding.
  const inMemoryOrdinaryDaysOnRamp = isInMemory && themeId === 'ordinary-days' && theme.inMemory;

  let questions;
  if (branchType === 'observer') {
    // Observer: prepend observer overlay before core.
    // For their-quirks-and-humor, hoist TPQH-11-OB (signature move in a meeting) to slot 2 to break
    // the run of three back-to-back competence asks before any quirk/scene question.
    // Fix (rank 4): TPQH-3 ("the face they make") is an intimate core question written for close
    // relationships (spouse/best friend/child); it assumes interior-state access that a colleague
    // plausibly does not have. Do NOT hoist TPQH-3 into the observer slot 2. Use TPQH-11-OB instead,
    // which is already scoped for the workplace/observer vantage.
    if (themeId === 'their-quirks-and-humor' && branchOverlay.length >= 3) {
      // branchOverlay: [TPQH-9-OB, TPQH-10-OB, TPQH-11-OB]
      // Build: [TPQH-9-OB, TPQH-10-OB, TPQH-11-OB, ...core-without-TPQH-1 (already stripped above)]
      questions = [...branchOverlay, ...coreQuestions];
    } else {
      questions = [...branchOverlay, ...coreQuestions];
    }
  } else if (!isInMemory && themeId === 'what-makes-them-them' && branchType === 'child') {
    // Interleave first child-overlay question at slot 2 so child vantage appears early
    const firstBranch = branchOverlay[0];
    const remainingBranch = branchOverlay.slice(1);
    const coreWithGap = [...coreQuestions];
    coreWithGap.splice(2, 0, firstBranch);
    questions = [...coreWithGap, ...remainingBranch];
  } else if (!isInMemory && themeId === 'what-makes-them-them' && branchType === 'grandchild') {
    // Grandchild variety: keep signature-detail (TPMT-7-GC) at slot 0, pull TPMT-8-GC to slot 2
    // so the first three are: signature-detail, core-q1, taught/carried-forward, ordinary texture
    const firstBranch = branchOverlay[0]; // TPMT-7-GC (softened "whenever you were around" version)
    const secondBranch = branchOverlay.slice(1); // TPMT-8-GC
    const coreWithGap = [...coreQuestions];
    if (secondBranch.length > 0) {
      coreWithGap.splice(2, 0, secondBranch[0]);
    }
    questions = [firstBranch, ...coreWithGap];
  } else if (branchOverlay.length > 0) {
    if (inMemoryOrdinaryDaysOnRamp) {
      // 3-slot on-ramp: [TPOD-1-IM, grounding-q, TPOD-2-IM, ...rest]
      // The branch overlay for ordinary-days is always [TPOD-7-X, TPOD-8-X].
      // Grief pacing (rank 5): for the partner branch, prefer TPOD-7-P (lighter morning scene)
      // for the slot-1 hoist and let TPOD-8-P (loss-framed "what you will miss") land at slot 3-4.
      // For all other branches: prefer index 1 (TPOD-8-X, the "habit/something they always did")
      // for the hoist -- avoids triple "describe a day" asks.
      const tpod1im = coreQuestions[0]; // TPOD-1-IM
      const tpod2im = coreQuestions[1]; // TPOD-2-IM
      const rest = coreQuestions.slice(2); // TPOD-3..6 (modified)
      let hoistBranchScene;
      if (branchType === 'partner' && branchOverlay.length >= 2) {
        // Partner: hoist the lighter TPOD-7-P (morning scene) to slot 1; TPOD-8-P goes later
        hoistBranchScene = branchOverlay[0]; // TPOD-7-P
      } else {
        // All others: hoist index 1 (the "habit/missing" question) to slot 1
        hoistBranchScene = branchOverlay.length > 1 ? branchOverlay[1] : branchOverlay[0];
      }
      const remainingBranchOverlay = branchOverlay.filter(q => q !== hoistBranchScene);
      // Build: [TPOD-1-IM, hoisted-grounding-q, TPOD-2-IM, ...rest interleaved with remaining]
      const coreWithBranch = [...rest];
      remainingBranchOverlay.forEach((bq, i) => {
        coreWithBranch.splice(i * 2, 0, bq);
      });
      questions = [tpod1im, hoistBranchScene, tpod2im, ...coreWithBranch];
    } else {
      // Non-observer: interleave the first branch question at slot 0, the second at slot 2.
      // This guarantees at least one relationship-voiced question in the first three.
      const firstBranch = branchOverlay[0];
      const remainingBranch = branchOverlay.slice(1);
      // Insert firstBranch at position 0, second branch question (if any) at position 2
      const coreWithGaps = [...coreQuestions];
      if (remainingBranch.length > 0) {
        coreWithGaps.splice(2, 0, remainingBranch[0]);
      }
      questions = [firstBranch, ...coreWithGaps, ...remainingBranch.slice(1)];
    }
  } else {
    questions = coreQuestions;
  }

  // Append IN_MEMORY-specific closing questions on Theme 7
  if (isInMemory && themeId === 'what-id-want-the-world-to-know' && theme.inMemory) {
    questions = [...questions, ...theme.inMemory];
  }

  return questions;
}

// ---------------------------------------------------------------------------
// getThirdPersonThemeOrder -- occasion-aware theme ordering
// ---------------------------------------------------------------------------

/**
 * Returns the recommended theme order for a given occasion.
 * The engine serves themes in this order; contributors will see questions from
 * the first theme before moving on to the second, etc.
 *
 * For short sessions (3-question minimum), the first 2-3 themes in the returned
 * order are the priority set.
 *
 * @param {string} [occasionId]  -- from OCCASION_TYPES
 * @returns {string[]}  -- ordered array of theme ids
 */
export function getThirdPersonThemeOrder(occasionId = '', branchType = 'observer') {
  const DESCENDANT_BRANCHES = ['child', 'grandchild', 'parent', 'sibling'];
  const isDescendant = DESCENDANT_BRANCHES.includes(branchType);

  switch (occasionId) {
    case 'IN_MEMORY':
      return [
        'ordinary-days',
        'a-moment-ill-never-forget',
        'what-makes-them-them',
        'their-quirks-and-humor',
        'what-they-taught-me',
        'how-i-met-them',
        'what-id-want-the-world-to-know',
      ];

    case 'MOTHERS_DAY':
    case 'FATHERS_DAY':
    case 'GRANDPARENTS_DAY':
      if (isDescendant) {
        // Descendants cannot answer "how I met them" -- lead with what-makes-them-them
        // then what-they-taught-me (strong for family occasions), then ordinary days.
        return [
          'what-makes-them-them',
          'what-they-taught-me',
          'their-quirks-and-humor',
          'ordinary-days',
          'a-moment-ill-never-forget',
          'how-i-met-them',
          'what-id-want-the-world-to-know',
        ];
      }
      // Non-descendant (partner writing about co-parent, friend, etc.) -- original order
      return [
        'how-i-met-them',
        'what-makes-them-them',
        'what-they-taught-me',
        'ordinary-days',
        'a-moment-ill-never-forget',
        'their-quirks-and-humor',
        'what-id-want-the-world-to-know',
      ];

    case 'BIRTHDAY':
    case 'APPRECIATION':
      // Celebration: warmth-first. Lead with quirks/humor then what-makes-them-them.
      // Do NOT open with a-moment-ill-never-forget (eulogy cadence).
      return [
        'their-quirks-and-humor',
        'what-makes-them-them',
        'a-moment-ill-never-forget',
        'how-i-met-them',
        'ordinary-days',
        'what-they-taught-me',
        'what-id-want-the-world-to-know',
      ];

    case 'RETIREMENT':
    case 'GRADUATION':
      if (branchType === 'observer') {
        // Colleague/observer: lead with witnessed workplace questions
        return [
          'their-quirks-and-humor',
          'what-makes-them-them',
          'what-they-taught-me',
          'a-moment-ill-never-forget',
          'how-i-met-them',
          'ordinary-days',
          'what-id-want-the-world-to-know',
        ];
      }
      // Family/friend writing for retirement: reflective lead is OK
      return [
        'what-makes-them-them',
        'what-they-taught-me',
        'a-moment-ill-never-forget',
        'their-quirks-and-humor',
        'how-i-met-them',
        'ordinary-days',
        'what-id-want-the-world-to-know',
      ];

    default:
      return [
        'how-i-met-them',
        'what-makes-them-them',
        'a-moment-ill-never-forget',
        'what-they-taught-me',
        'their-quirks-and-humor',
        'ordinary-days',
        'what-id-want-the-world-to-know',
      ];
  }
}

// ---------------------------------------------------------------------------
// getOccasionOpeningQuestion -- occasion-seeded Q0 opener (rank 3 mechanism)
// ---------------------------------------------------------------------------

/**
 * Returns a single occasion-aware opening question to prepend as Q0 before
 * the first theme question. Returns null when no opener is needed.
 *
 * This is the mechanism for rank 3 (occasion must bend question TEXT, not just order).
 * The opener references the occasion directly so the book's purpose is immediately clear.
 *
 * @param {string} occasionId
 * @param {string} branchType
 * @returns {{id: string, text: string}|null}
 */
export function getOccasionOpeningQuestion(occasionId, branchType) {
  if (!occasionId || occasionId === 'IN_MEMORY') return null;

  // OCCASION_OPENERS -- Q0 opener table.
  //
  // Invariants (asserted by RENDER-PATH tests):
  //   1. Every opener uses [Name] (never hard-codes a relationship word like "your mom").
  //   2. No opener pulls the same scene-target as the first theme question served after it.
  //
  // Fix (rank 1):
  //   TPOCC-MD-C: was the only opener missing [Name] ("your mom"). Fixed to mirror TPOCC-FD-C.
  //   TPOCC-RT-OB: "most known for" duplicates TPQH-9-OB "known for at work" back-to-back (HIGH).
  //               Re-aimed to a moment-at-work ask (mirrors TPOCC-RT-DEF style), so TPQH-9-OB
  //               can lead the theme cleanly. Q0_COLLISION_SUPPRESSES entry retained as safety net.
  //   TPOCC-BD-SB: "memory that makes you smile / real her" overlaps TPQH-9-SB "quirk from childhood".
  //               Re-aimed to a celebration-scene ask (distinct from childhood quirk territory).
  //   TPOCC-BD-F:  "favorite memory" is a near-clone of TPOCC-BD-DEF and pre-claims the same ground
  //               as TPQH-9-F "inside joke with its own story". Re-aimed to a non-overlapping scene ask.
  //   TPOCC-GPD-GC: "favorite memory" overlaps TPMT-7-GC grandchild signature-memory ask.
  //               Re-aimed to a visit/scene ask distinct from the signature-habit question.
  const OCCASION_OPENERS = {
    MOTHERS_DAY: {
      // Fix: was "What is a favorite memory of your mom" -- only opener missing [Name].
      // Mirrored to TPOCC-FD-C style.
      child:      { id: 'TPOCC-MD-C',  text: "What is a favorite memory of [Name], the one that comes back on its own and makes you smile?" },
      grandchild: { id: 'TPOCC-MD-GC', text: "What is your favorite memory of [Name]? The one that comes back on its own." },
      default:    null,
    },
    FATHERS_DAY: {
      child:      { id: 'TPOCC-FD-C',  text: "What is an early memory of [Name], the first one that surfaces when you think of [them]?" },
      grandchild: { id: 'TPOCC-FD-GC', text: "What is a memory of [Name] you could tell just from being around [them], something that is completely [theirs]?" },
      default:    null,
    },
    BIRTHDAY: {
      // Fix: was "favorite memory with [Name]" -- near-clone of default and pre-claims same
      // ground as TPQH-9-F "inside joke". Re-aimed to a celebration-scene ask.
      friend:     { id: 'TPOCC-BD-F',  text: "Think of a moment with [Name] that made you think, this is exactly who [Name] is. It doesn't have to be a big occasion. What comes back?" },
      // Fix: was "memory that makes you smile / the real her" -- overlaps TPQH-9-SB "quirk from childhood".
      // Re-aimed to a celebration-scene ask that doesn't pre-claim the signature/quirk territory.
      sibling:    { id: 'TPOCC-BD-SB', text: "What is a moment with [Name] you want [them] to know you have not forgotten, something that is just between the two of you?" },
      child:      { id: 'TPOCC-BD-C',  text: "What is a moment with [Name] that feels completely like [them], something you want [them] to know you have not forgotten?" },
      default:    { id: 'TPOCC-BD-DEF', text: "What is your favorite memory with [Name]? The first one that comes to mind." },
    },
    RETIREMENT: {
      // Fix: was "What is [Name] most known for" -- near-verbatim duplicate of TPQH-9-OB
      // "something [Name] was known for at work". Re-aimed to a moment-at-work ask so TPQH-9-OB
      // can open the theme without repetition.
      observer:   { id: 'TPOCC-RT-OB', text: "What is a moment you watched [Name] at work that has stayed with you, something that tells you who [Name] really is?" },
      default:    { id: 'TPOCC-RT-DEF', text: "What is a moment with [Name] at work that has stayed with you?" },
    },
    GRANDPARENTS_DAY: {
      // Fix: was "favorite memory of [Name] / comes back when you picture being with them" --
      // overlaps TPMT-7-GC grandchild "signature habit/phrase" ask. Re-aimed to a visit-scene
      // ask that is distinct from the signature-habit territory.
      grandchild: { id: 'TPOCC-GPD-GC', text: "Picture a visit to [Name], one of the ordinary ones. What comes back when you put yourself there?" },
      default:    null,
    },
  };

  const occasionMap = OCCASION_OPENERS[occasionId];
  if (!occasionMap) return null;
  const opener = occasionMap[branchType] || occasionMap.default;
  return opener || null;
}

// ---------------------------------------------------------------------------
// getThemeDisplayName -- branch-aware theme display name
// ---------------------------------------------------------------------------

/**
 * Returns the display name for a theme, accounting for branch-specific overrides.
 * Falls back to theme.name for themes that don't override.
 * When recipientName is provided, [Name] tokens in branchNames are resolved.
 *
 * @param {string} themeId
 * @param {string} [branchType]
 * @param {string} [recipientName]
 * @param {object|null} [pronouns]
 * @returns {string}
 */
export function getThemeDisplayName(themeId, branchType = 'observer', recipientName = '', pronouns = null) {
  const theme = THIRD_PERSON_THEMES.find(t => t.id === themeId);
  if (!theme) return '';
  let raw;
  if (theme.branchNames && theme.branchNames[branchType]) {
    raw = theme.branchNames[branchType];
  } else {
    raw = theme.name;
  }
  return resolveThirdPersonPromptText(raw, recipientName, pronouns);
}

// ---------------------------------------------------------------------------
// getMemoryModeOnRamp -- grief on-ramp for third-person-memory mode
// ---------------------------------------------------------------------------

/**
 * Returns a single gentle on-ramp line to prepend before the first theme question
 * when mode === 'third-person-memory'. Does not open on the death; just adds
 * the breath before questions begin.
 *
 * @param {string} recipientName
 * @returns {string}
 */
export function getMemoryModeOnRamp(recipientName = '') {
  const name = (recipientName && recipientName.trim()) ? recipientName.trim() : 'them';
  return `Whenever you are ready. There is no rush here.`;
}

// ---------------------------------------------------------------------------
// resolveThirdPersonPromptText -- [Name] substitution helper
// ---------------------------------------------------------------------------

/**
 * Replaces [Name] in a question text with the recipient's name.
 * Falls back to 'them' when recipientName is blank.
 *
 * Supports [parentRole] token (fix rank 5) -- resolved from occasion + pronouns:
 *   FATHERS_DAY / he-him -> "your dad"
 *   MOTHERS_DAY / she-her -> "your mom"
 *   otherwise -> "your mom or dad"
 * Pass { occasionId, pronouns } in the options object to enable resolution.
 *
 * @param {string} text
 * @param {string} [recipientName]
 * @param {object|null} [pronouns]
 * @param {object} [opts]
 * @param {string} [opts.occasionId]
 * @returns {string}
 */
export function resolveThirdPersonPromptText(text, recipientName = '', pronouns = null, opts = {}) {
  if (!text) return '';
  const name = (recipientName && recipientName.trim()) ? recipientName.trim() : 'them';
  // pronoun defaults
  const subj  = (pronouns && pronouns.subject)     ? pronouns.subject     : 'they';
  const obj   = (pronouns && pronouns.object)      ? pronouns.object      : 'them';
  const poss  = (pronouns && pronouns.possessive)  ? pronouns.possessive  : 'their';
  const objPoss = (pronouns && pronouns.objectPoss) ? pronouns.objectPoss : 'theirs';
  // reflexive: herself / himself / themselves
  const refl = pronouns
    ? (pronouns.subject === 'she' ? 'herself' : pronouns.subject === 'he' ? 'himself' : 'themselves')
    : 'themselves';

  // [parentRole] token: resolved from occasion+pronouns for TPMT-7-C (fix rank 5).
  // "your dad" when occasion is FATHERS_DAY or subject pronouns are he/him.
  // "your mom" when occasion is MOTHERS_DAY or subject pronouns are she/her.
  // Fallback "your mom or dad" when genuinely unknown.
  let parentRole = 'your mom or dad';
  if (opts && opts.occasionId) {
    if (opts.occasionId === 'FATHERS_DAY') parentRole = 'your dad';
    else if (opts.occasionId === 'MOTHERS_DAY') parentRole = 'your mom';
  }
  if (parentRole === 'your mom or dad' && pronouns) {
    if (pronouns.subject === 'he') parentRole = 'your dad';
    else if (pronouns.subject === 'she') parentRole = 'your mom';
  }

  return text
    .replace(/\[Name\]/g, name)
    .replace(/\[parentRole\]/g, parentRole)
    .replace(/\[they\]/g, subj)
    .replace(/\[them\]/g, obj)
    .replace(/\[their\]/g, poss)
    .replace(/\[theirs\]/g, objPoss)
    .replace(/\[themself\]/g, refl)
    .replace(/\[she\]/g, subj)
    .replace(/\[he\]/g, subj)
    .replace(/\[her\]/g, obj)
    .replace(/\[him\]/g, obj)
    .replace(/\[hers\]/g, objPoss)
    .replace(/\[his\]/g, objPoss);
}
